import React, { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { faRobot } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Button, Group, HoverCard, List, Text } from "@mantine/core";

import {
  useBasePath,
  useFetchAiTaskState,
  useLicenses,
  useSupportsFeature,
} from "@shared/hooks";
import { DeploymentProductConfig, FindingsFilters } from "@shared/types";

import { useTriageActions } from "../../stores";
import { AssistantDialog } from "../AssistantDialog/AssistantDialog";
interface AssistantButtonProps {
  numSelected: number;
  deploymentId: number;
  productConfig?: DeploymentProductConfig;
  selectedFindingIds: Set<number>;
  filterOverrides?: FindingsFilters;
  showAnalyzeNumber: boolean;
  buttonSize: string;
}

export const AssistantButton: FC<AssistantButtonProps> = ({
  numSelected,
  deploymentId,
  productConfig,
  selectedFindingIds,
  filterOverrides,
  showAnalyzeNumber,
  buttonSize,
}) => {
  const [open, setOpen] = useState(false);

  const { resetFindings } = useTriageActions();
  const basePath = useBasePath();
  const { data: taskData, refetch } = useFetchAiTaskState();

  const { data: licenses = [] } = useLicenses();

  const isAssistantPaidDeployment = useSupportsFeature(
    "isAssistantPaidDeployment"
  );

  const hasActiveLicense = useMemo(() => {
    return (
      licenses.filter((l) => l.active).length > 0 || isAssistantPaidDeployment
    );
  }, [licenses, isAssistantPaidDeployment]);

  const isDisabled = useMemo(() => {
    return (
      numSelected <= 0 ||
      (!hasActiveLicense && numSelected >= (taskData?.runsRemaining ?? 50)) ||
      !productConfig?.ai.enabled
    );
  }, [
    numSelected,
    productConfig?.ai.enabled,
    taskData?.runsRemaining,
    hasActiveLicense,
  ]);

  const tooltipContent = useMemo(() => {
    return (
      <Text fz="sm" c="white" style={{ wordBreak: "break-word" }}>
        {!hasActiveLicense &&
        numSelected >= (taskData?.runsRemaining ?? 50) &&
        productConfig?.ai.enabled ? (
          <div data-testid="too-many-findings-assistant">
            This organization has {taskData?.runsRemaining ?? 50} Assistant runs
            remaining this month. Select {taskData?.runsRemaining ?? 50}{" "}
            findings or fewer.
            <List fz="sm">
              <List.Item>
                Paid accounts get 10000 Assistant runs a month.
              </List.Item>
            </List>
            <Anchor
              inherit
              href="/pricing"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about paid features here.
            </Anchor>
          </div>
        ) : !productConfig?.ai.enabled ? (
          <div data-testid="not-enabled-assistant">
            Assistant is not enabled for this organization.<br></br> Assistant
            can:
            <List fz="sm">
              <List.Item>
                Recommend either "should fix" or "ignore" for each finding
              </List.Item>
              <List.Item>
                Construct remediation guidance, if applicable
              </List.Item>
              <List.Item>Generate code for a fix, if applicable</List.Item>
              <List.Item>
                Tag findings by component (e.g., auth, payments)
              </List.Item>
            </List>
            <Group justify="flex-end">
              <Button
                variant="filled"
                justify="end"
                fz="sm"
                size="compact-md"
                mt={5}
                component={Link}
                to={`${basePath}/settings?openAssistantInstall`}
              >
                Sign up
              </Button>
            </Group>
          </div>
        ) : selectedFindingIds.size > 0 ? (
          <div data-testid="correct-case-assistant">
            Analyze finding with Assistant. <br />
            Assistant will:
            <List fz="sm">
              <List.Item>
                Recommend either "should fix" or "ignore" for each finding
              </List.Item>
              <List.Item>
                Construct remediation guidance, if applicable
              </List.Item>
              <List.Item>Generate code for a fix, if applicable</List.Item>
              <List.Item>
                Tag findings by component (e.g., auth, payments)
              </List.Item>
            </List>
          </div>
        ) : (
          <div data-testid="correct-case-assistant">
            Select findings to analyze with Assistant. <br />
            Assistant will:
            <List fz="sm">
              <List.Item>
                Recommend either "should fix" or "ignore" for each finding
              </List.Item>
              <List.Item>
                Construct remediation guidance, if applicable
              </List.Item>
              <List.Item>Generate code for a fix, if applicable</List.Item>
              <List.Item>
                Tag findings by component (e.g., auth, payments)
              </List.Item>
            </List>
          </div>
        )}
      </Text>
    );
  }, [
    numSelected,
    taskData?.runsRemaining,
    hasActiveLicense,
    productConfig?.ai.enabled,
    basePath,
    selectedFindingIds.size,
  ]);

  return (
    <>
      <HoverCard width={350} withArrow withinPortal>
        <HoverCard.Target>
          <Button
            size={buttonSize}
            variant="outline"
            data-disabled={isDisabled}
            // Getting around the hover target not working if the button is disabled
            onClick={() => {
              if (!isDisabled) setOpen(true);
            }}
          >
            <>
              <FontAwesomeIcon
                size="sm"
                icon={faRobot}
                style={{ marginBottom: "2px" }}
              />
              {showAnalyzeNumber ? (
                <Text inherit ml="xs">
                  Analyze ({numSelected})
                </Text>
              ) : (
                <Text inherit ml="xs">
                  Analyze
                </Text>
              )}
            </>
          </Button>
        </HoverCard.Target>
        <HoverCard.Dropdown bg="dark.7">{tooltipContent}</HoverCard.Dropdown>
      </HoverCard>
      {open && (
        <AssistantDialog
          deploymentId={deploymentId}
          filterOverrides={filterOverrides}
          numFindings={numSelected}
          onClose={() => setOpen(false)}
          runsRemaining={taskData?.runsRemaining ?? 50}
          waitTime={taskData?.waitTime ?? 0}
          onSaveClick={() => {
            resetFindings();
            refetch();
            setOpen(false);
          }}
          findingIds={selectedFindingIds}
          opened={open}
          hasActiveLicense={hasActiveLicense}
        />
      )}
    </>
  );
};
